import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const ROLES = ["admin", "back_office", "lawyer"];
const ROLE_LABEL = {
  admin:       "Admin — full access (pricing, users, all matters)",
  back_office: "Back Office — execution layer",
  lawyer:      "Lawyer — exception-focused view",
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", role: "back_office", password: "" });
  const [creating, setCreating] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-10 max-w-2xl mx-auto" data-testid="admin-users-forbidden">
        <div className="bg-amber-50 border border-amber-200 p-6">
          <h2 className="font-serif text-2xl">Admin only</h2>
          <p className="text-sm text-zinc-700 mt-2">User management is restricted to the admin role.</p>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Created ${form.role} user`);
      setForm({ email: "", name: "", role: "back_office", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-10 max-w-5xl mx-auto" data-testid="admin-users">
      <div className="mb-10">
        <div className="text-overline text-zinc-500">Team</div>
        <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">Users & roles</h1>
        <p className="mt-2 text-sm text-zinc-600">Three roles: <b>admin</b>, <b>back_office</b>, <b>lawyer</b>.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="px-6 py-4 border-b border-zinc-100 text-overline text-zinc-500">Team members</div>
          {loading ? (
            <div className="px-6 py-10 text-zinc-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-100" data-testid={`user-row-${u.email}`}>
                    <td className="px-6 py-3">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-xs text-zinc-500">{u.email}</div>
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-[10px] uppercase tracking-widest bg-zinc-100 px-2 py-1">{u.role}</span>
                    </td>
                    <td className="px-6 py-3 text-xs text-zinc-500 text-right">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <form onSubmit={submit} className="bg-zinc-900 text-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-overline text-zinc-400">
            <ShieldCheck className="h-3 w-3" /> Create user
          </div>
          <div>
            <Label className="text-zinc-400">Full name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white rounded-none mt-1" data-testid="new-user-name" />
          </div>
          <div>
            <Label className="text-zinc-400">Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white rounded-none mt-1" data-testid="new-user-email" />
          </div>
          <div>
            <Label className="text-zinc-400">Password (min 6)</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white rounded-none mt-1" data-testid="new-user-password" />
          </div>
          <div>
            <Label className="text-zinc-400">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-none bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="new-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={creating} className="w-full rounded-none bg-white text-zinc-900 hover:bg-zinc-100" data-testid="new-user-submit">
            {creating ? "Creating…" : "Create user"}
          </Button>
        </form>
      </div>
    </div>
  );
}
